import { List, ListItem, Tooltip, Typography, TypographyProps } from '@mui/material';
import { useMemo } from 'react';

const BEAUFORT_MAX_KTS = [1, 3, 6, 10, 16, 21, 25, 33, 40, 47, 55, 63, 73] as const;
const BEAUFORT_COLOR = [
    '#FFFFFF',
    '#AEF1F9',
    '#96F7DC',
    '#96F7B4',
    '#6FF46F',
    '#73ED12',
    '#A4ED12',
    '#DAED12',
    '#EDC212',
    '#ED8F12',
    '#ED6312',
    '#ED2912',
    '#D5102D',
] as const;

const BEAUFORT_DESCRIPTION = [
    'Calm',
    'Light Air',
    'Light Breeze',
    'Gentle Breeze',
    'Moderate Breeze',
    'Fresh Breeze',
    'Strong Breeze',
    'Moderate or Near Gale',
    'Gale or Fresh Gale',
    'Strong Gale',
    'Whole Gale or Storm',
    'Violent Storm',
    'Hurricane',
];

const BEAUFORT_EFFECTS = [
    ['Still, calm air, smoke will rise vertically.', 'Water is mirror-like.'],
    ['Rising smoke drifts, wind vane is inactive.', 'Small ripples appear on water surface.'],
    [
        'Leaves rustle, can feel wind on your face, wind vanes begin to move.',
        'Small wavelets develop, crests are glassy.',
    ],
    [
        'Leaves and small twigs move, light weight flags extend.',
        'Large wavelets, crests start to break, some whitecaps.',
    ],
    ['Small branches move, raises dust, leaves and paper.', 'Small waves develop, becoming longer, whitecaps.'],
    ['Small trees sway.', 'White crested wavelets (whitecaps) form, some spray.'],
    [
        'Large tree branches move,  telephone wires begin to “whistle”, umbrellas are difficult to keep under control.',
        'Larger waves form, whitecaps prevalent, spray.',
    ],
    [
        'Large trees sway, becoming difficult to walk.',
        'Larger waves develop, white foam from breaking waves begins to be blown.',
    ],
    [
        'Twigs and small branches are broken from trees, walking is difficult.',
        'Moderately large waves with blown foam.',
    ],
    [
        'Slight damage occurs to buildings, shingles are blown off of roofs.',
        'High waves (6 meters), rolling seas, dense foam, Blowing spray reduces visibility.',
    ],
    [
        'Trees are broken or uprooted, building damage is considerable.',
        'Large waves (6-9 meters), overhanging crests, sea becomes white with foam, heavy rolling, reduced visibility.',
    ],
    ['Extensive widespread damage.', 'Large waves (9-14 meters), white foam, visibility further reduced.'],
    [
        'Extreme destruction, devastation.',
        'Large waves over 14 meters, air filled with foam, sea white with foam and driving',
    ],
];

export const BeaufortNumber = ({ windSpeedKnots, sx, ...props }: { windSpeedKnots: number } & TypographyProps) => {
    const beaufort = useMemo(() => {
        for (let i = 0; i < BEAUFORT_MAX_KTS.length; i++) {
            if (windSpeedKnots < BEAUFORT_MAX_KTS[i]) {
                return i;
            }
        }
        return BEAUFORT_MAX_KTS.length - 1;
    }, [windSpeedKnots]);
    return (
        <Tooltip
            title={
                <List
                    sx={{
                        py: 0,
                        px: 0.5,
                        '& > .MuiListItem-root': { py: 0.5, px: 0 },
                    }}
                >
                    <ListItem>Beaufort number {beaufort}</ListItem>
                    <ListItem sx={{ fontWeight: 'bold' }}>{BEAUFORT_DESCRIPTION[beaufort]}</ListItem>
                    {BEAUFORT_EFFECTS[beaufort].map((s) => (
                        <ListItem key={s}>{s}</ListItem>
                    ))}
                </List>
            }
        >
            <Typography
                sx={{
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    minWidth: '1.5em',
                    textAlign: 'center',
                    backgroundColor: BEAUFORT_COLOR[beaufort],
                    ...sx,
                }}
                {...props}
            >
                {beaufort}
            </Typography>
        </Tooltip>
    );
};
